/**
 * useAgentSession — der wiederverwendbare Chat-Kern des MAESTRO-Assistenten
 * (docs/KONZEPT-AGENT-NATIVE.md, Phase 0). VERHALTENSNEUTRAL aus pages/assistent.tsx extrahiert:
 * dieselben Endpunkte, dieselbe „nach jeder Mutation loadHistory()"-Semantik, KEIN Streaming.
 *
 * Kapselt: messages, cards, busy, loading, error + send/runAction/approve/reject/loadHistory.
 * Genutzt von der Fallback-Seite (pages/assistent.tsx) UND — als EINE geteilte Instanz im
 * CopilotProvider — von der global angedockten Copilot-Spalte (ui/CopilotPanel).
 *
 * Endpunkte:
 *   GET  /api/agent/history                         -> { messages, cards }
 *   POST /api/agent/message  { text, attachments, activeRecord }  -> { reply, cards, done }
 *   POST /api/agent/action   { action, … }          -> { cards }
 *   POST /api/agent/approve  { executionId, cardOverride } -> { cards }
 *   POST /api/agent/reject   { executionId, reason }  -> { cards }
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { apiFetch, toApiError } from "../lib/api";
import { fileToBase64 } from "../lib/files";
import { routeToRecord, type ActiveRecord } from "../lib/routeToRecord";
import type { AgentCard, CardOverride } from "../ui/agent-cards";

export interface TextPart {
  type: string;
  text?: string;
}
export interface AgentMessage {
  id: string;
  role: string;
  parts?: TextPart[];
}
/** P5: eine Zeile je Werkzeugaufruf - was getan wurde, woran, mit welchem Ergebnis. */
export interface Schrittzeile {
  ok: boolean;
  text: string;
  at: number;
}
interface HistoryData {
  messages: AgentMessage[];
  cards: AgentCard[];
  steps?: Schrittzeile[];
}

/** Sichtbarer Text einer Nachricht (nur text-Parts, zusammengefügt und getrimmt). */
export function textOf(m: AgentMessage): string {
  return (m.parts ?? [])
    .filter((p) => p.type === "text" && typeof p.text === "string")
    .map((p) => p.text ?? "")
    .join("")
    .trim();
}

/** Minimales Markdown-Stripping für die Klartext-Anzeige der Agent-Antworten. */
export function stripMarkdown(s: string): string {
  return s.replace(/\*\*/g, "").replace(/^\s{0,3}#{1,6}\s+/gm, "").replace(/`/g, "");
}

/**
 * Erkennt reportContext()-Nachrichten (Slice 3, Rueckkanal Seite -> Copilot): der Agent-Worker
 * haengt sie als role:"user" an die Historie (damit das Modell darauf reagiert), obwohl sie NIE
 * vom Nutzer stammen - der [SEITEN-...]-Praefix markiert das ausdruecklich als interne Notiz.
 * Ohne diese Erkennung rendert die Chat-UI den rohen Instruktionstext (der fuers Modell gedacht
 * ist, nicht fuer Menschen) wie eine echte Chatnachricht - siehe docs/KONZEPT-ANGEBOTS-BUILDER.md
 * §5.1. `.includes("INTERNE SYSTEMMELDUNG")` zusaetzlich zum Praefix, damit ein zufaellig mit "["
 * beginnender echter Nutzertext nie faelschlich herausgefiltert wird.
 */
export function internalContextLabel(text: string): string | null {
  if (!text.startsWith("[SEITEN-") || !text.includes("INTERNE SYSTEMMELDUNG")) return null;
  if (text.startsWith("[SEITEN-ERFOLG,")) return "Auf der Seite hat das gerade geklappt — ich wurde informiert.";
  return "Auf der Seite ist etwas schiefgelaufen — ich wurde informiert.";
}

async function getData<T>(path: string): Promise<T> {
  const res = await apiFetch(path);
  if (!res.ok) throw await toApiError(res);
  return ((await res.json()) as { data: T }).data;
}
async function postData<T>(path: string, payload: unknown): Promise<T> {
  const res = await apiFetch(path, { method: "POST", body: JSON.stringify(payload) });
  if (!res.ok) throw await toApiError(res);
  return ((await res.json()) as { data: T }).data;
}

/**
 * Slice 3 (Fehler-in-den-Chat, §6.4) — strukturierte Meldung eines Seiten-Fehlers (Klick, nicht
 * Chat) an den Copilot. surface/action/errorCode sind bewusst kurze Kennungen, KEIN Freitext: der
 * Agent-Worker uebersetzt errorCode serverseitig ueber PREREQUISITE_MAP, nichts hiervon geht roh
 * an das Modell.
 */
export interface ReportContextPayload {
  surface: "offer" | "import" | "catalog" | "payments" | "settings" | "calendar" | "inquiries" | "other";
  action: string;
  errorCode: string;
  /** §5.1b: eine zuvor per reportContext gemeldete Fehlermeldung nachtraeglich entwerten - der
   * Betreiber hat dieselbe Aktion inzwischen erfolgreich abgeschlossen. Optional, Default-Verhalten
   * (Fehlermeldung) bleibt unveraendert, wenn weggelassen. */
  outcome?: "success";
}

export interface AgentSession {
  messages: AgentMessage[];
  cards: AgentCard[];
  /** P5: eine Zeile je Werkzeugaufruf, aelteste zuerst. Bleibt ueber Turns hinweg stehen. */
  steps: Schrittzeile[];
  busy: boolean;
  loading: boolean;
  error: string | null;
  /** Für UI-seitige Meldungen (z. B. „Datei zu groß") in denselben Fehlerkanal. */
  setError: (message: string | null) => void;
  /** Sendet Text + optionale Dateianhänge. Liefert `true` bei erfolgreichem Versand. */
  send: (text: string, attachments?: File[]) => Promise<boolean>;
  runAction: (payload: Record<string, unknown>) => Promise<void>;
  approve: (executionId: string, cardOverride?: CardOverride) => Promise<void>;
  reject: (executionId: string, reason: string) => Promise<void>;
  loadHistory: () => Promise<void>;
  /** Slice 3: Seiten-Fehler an den Copilot melden (best-effort, wirft nie). */
  reportContext: (payload: ReportContextPayload) => Promise<void>;
}

/**
 * Optionale Signale, die der Agent laut Response-Vertrag (A2) mit jeder Antwort mitschicken kann.
 * Sie stehen im ausgepackten `data` der Endpunkte message/action/approve.
 */
export interface AgentSignals {
  /** Pfad, den der Content-Bereich oeffnen soll (z. B. "/katalog/import?importId=<id>"). */
  navigate?: string;
  /** „Daten geaendert" — die betroffene Seite refetcht die betroffene Ressource/Zeile. */
  changed?: { resource: string; id?: string };
}

/**
 * Callbacks, die der CopilotProvider hereinreicht, damit die Session navigate/changed an den
 * Event-Bus weitergibt (navigate -> requestNavigate, changed -> emitContentChanged). Optional,
 * damit die Fallback-Seite (pages/assistent.tsx) die Session weiterhin ohne Bus nutzen kann.
 */
export interface AgentSessionOptions {
  onNavigate?: (path: string) => void;
  onContentChanged?: (payload: { resource: string; id?: string }) => void;
  // 23e: aktive Unterhaltung. undefined/null = Legacy-/Default-Sitzung - identisches
  // Verhalten wie vor 23e (der Server macht dieselbe Unterscheidung in resolveAgent).
  conversationId?: string | null;
}

export function useAgentSession(opts: AgentSessionOptions = {}): AgentSession {
  const [messages, setMessages] = useState<AgentMessage[]>([]);
  const [cards, setCards] = useState<AgentCard[]>([]);
  // P5: Die Zeilen kommen bei JEDER Antwort mit und werden serverseitig gehalten - deshalb
  // ersetzen wir sie hier, statt anzuhaengen. Ohne die Persistenz im Agenten waeren sie nach dem
  // loadHistory() direkt hinter dem Senden wieder weg.
  const [steps, setSteps] = useState<Schrittzeile[]>([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Neueste Callbacks in einem Ref halten: so bleiben send/runAction/approve stabil (identische
  // Referenz), auch wenn der Provider bei jedem Render frische opts hereinreicht.
  const optsRef = useRef(opts);
  optsRef.current = opts;

  // S1.5 (KONZEPT-GEDAECHTNIS.md § 5): welchen Datensatz der Betreiber gerade offen hat, als
  // Ref gehalten (wie optsRef) - send() bleibt so referentiell stabil, obwohl sich die Route bei
  // jedem Seitenwechsel aendert. Reine Client-Behauptung; beforeTurn() prueft sie server-seitig
  // nach, bevor daraus Fakten/Playbook-Scope werden (apps/api/src/routes/gedaechtnis.ts).
  const location = useLocation();
  const activeRecordRef = useRef<ActiveRecord | null>(null);
  activeRecordRef.current = routeToRecord(location.pathname, location.search);
  const emitSignals = useCallback((s: AgentSignals) => {
    if (s.navigate) optsRef.current.onNavigate?.(s.navigate);
    if (s.changed) optsRef.current.onContentChanged?.(s.changed);
  }, []);

  // 23e: haengt ?conversationId=... an, wenn eine benannte Unterhaltung aktiv ist - fehlt sie,
  // ist der Pfad unveraendert (identisches Verhalten wie vor 23e, die eine geteilte Sitzung).
  const conversationId = opts.conversationId ?? null;
  const withConversation = useCallback(
    (path: string) => (conversationId ? `${path}?conversationId=${encodeURIComponent(conversationId)}` : path),
    [conversationId],
  );

  // W1 (BUGHUNT-2026-08-15): Ein Agent-Turn dauert 30-170 s, und die Knoepfe zum Wechseln/Anlegen
  // einer Unterhaltung sind waehrenddessen NICHT gesperrt. Loest der alte POST nach einem Wechsel
  // auf, riefe er das per Closure gebundene loadHistory() der ALTEN Unterhaltung auf und
  // ueberschriebe den gerade geladenen Verlauf der neuen - weitere Nachrichten gingen dann an die
  // falsche Unterhaltung. Deshalb prueft jeder Mutator NACH dem await, ob seine Unterhaltung noch
  // die aktive ist (optsRef traegt immer den frischesten Stand) - sonst wird die Antwort verworfen.
  const istNochAktuell = useCallback(() => (optsRef.current.conversationId ?? null) === conversationId, [conversationId]);

  const loadHistory = useCallback(async () => {
    try {
      const data = await getData<HistoryData>(withConversation("/api/agent/history"));
      setMessages(data.messages ?? []);
      setCards(data.cards ?? []);
      setSteps(data.steps ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [withConversation]);

  // 23e: bei einem Wechsel der Unterhaltung sofort leeren (kein Nachrichten-Flackern aus dem
  // alten Thread), bevor der Verlauf der neuen Unterhaltung geladen wird. Laeuft auch beim
  // ersten Mount (no-op, messages/cards sind bereits leer).
  useEffect(() => {
    setMessages([]);
    setCards([]);
    setLoading(true);
  }, [conversationId]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  const send = useCallback(
    async (text: string, attachments?: File[]): Promise<boolean> => {
      const trimmed = text.trim();
      if (!trimmed && (!attachments || attachments.length === 0)) return false;
      setBusy(true);
      setError(null);
      const label = trimmed || "(Datei angehängt)";
      // optimistische User-Nachricht. Die Kennung wird festgehalten, damit der Fehlerfall unten
      // GENAU diese Nachricht wieder entfernen kann (W2).
      const localId = `local-${Date.now()}`;
      setMessages((prev) => [...prev, { id: localId, role: "user", parts: [{ type: "text", text: label }] }]);
      try {
        const payload = attachments && attachments.length ? await Promise.all(attachments.map(fileToBase64)) : undefined;
        const res = await postData<{ reply: string; cards: AgentCard[]; done: boolean; steps?: Schrittzeile[] } & AgentSignals>(withConversation("/api/agent/message"), {
          text: label,
          attachments: payload,
          activeRecord: activeRecordRef.current,
        });
        // W1: Unterhaltung wurde waehrenddessen gewechselt -> Antwort verwerfen, nicht den
        // Verlauf der neuen Unterhaltung mit dem alten Thread ueberschreiben. Der Turn selbst
        // ist serverseitig gelungen (return true), er gehoert nur nicht mehr auf den Schirm.
        if (!istNochAktuell()) return true;
        setCards(res.cards ?? []);
        setSteps(res.steps ?? []);
        emitSignals(res);
        await loadHistory();
        return true;
      } catch (e) {
        // W2 (BUGHUNT-2026-08-15): die optimistische Nachricht wieder entfernen. Das Panel legt
        // den Text bei einem Fehlschlag zurueck ins Eingabefeld - bliebe die Nachricht zusaetzlich
        // im Verlauf stehen, saehe der Turn gesendet aus und ein erneutes Senden wirkte doppelt.
        setMessages((prev) => prev.filter((m) => m.id !== localId));
        if (!istNochAktuell()) return false;
        setError(e instanceof Error ? e.message : String(e));
        return false;
      } finally {
        setBusy(false);
      }
    },
    [loadHistory, emitSignals, withConversation, istNochAktuell],
  );

  const runAction = useCallback(async (payload: Record<string, unknown>) => {
    setBusy(true);
    setError(null);
    try {
      const res = await postData<{ cards: AgentCard[] } & AgentSignals>(withConversation("/api/agent/action"), payload);
      if (!istNochAktuell()) return; // W1: Unterhaltung inzwischen gewechselt
      setCards(res.cards ?? []);
      emitSignals(res);
    } catch (e) {
      if (!istNochAktuell()) return;
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [emitSignals, withConversation, istNochAktuell]);

  const approve = useCallback(
    async (executionId: string, cardOverride?: CardOverride) => {
      setBusy(true);
      setError(null);
      try {
        const res = await postData<{ cards: AgentCard[] } & AgentSignals>(withConversation("/api/agent/approve"), { executionId, cardOverride });
        if (!istNochAktuell()) return; // W1: Unterhaltung inzwischen gewechselt
        setCards(res.cards ?? []);
        emitSignals(res);
        await loadHistory();
      } catch (e) {
        if (!istNochAktuell()) return;
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(false);
      }
    },
    [loadHistory, emitSignals, withConversation, istNochAktuell],
  );

  // S1.7/E4: `reason` ist PFLICHT (die Route lehnt ohne ihn mit 422 ab) - ApprovalCard sammelt ihn
  // ueber eine Auswahlliste + optionalem Freitext, bevor sie reject() ueberhaupt aufruft.
  const reject = useCallback(async (executionId: string, reason: string) => {
    setBusy(true);
    setError(null);
    try {
      const res = await postData<{ cards: AgentCard[] }>(withConversation("/api/agent/reject"), { executionId, reason });
      if (!istNochAktuell()) return; // W1: Unterhaltung inzwischen gewechselt
      setCards(res.cards ?? []);
    } catch (e) {
      if (!istNochAktuell()) return;
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [withConversation, istNochAktuell]);

  // Slice 3: best-effort - ein Rueckkanal-Fehler darf die eigentliche Seiten-Fehlermeldung
  // (setFormError) NIE verdraengen. Schlaegt der Turn fehl, bleibt der Chat einfach unveraendert.
  const reportContext = useCallback(
    async (payload: ReportContextPayload) => {
      setBusy(true);
      try {
        const res = await postData<{ cards: AgentCard[] } & AgentSignals>(withConversation("/api/agent/report-context"), payload);
        if (!istNochAktuell()) return; // W1: Unterhaltung inzwischen gewechselt
        setCards(res.cards ?? []);
        emitSignals(res);
        await loadHistory();
      } catch {
        /* best-effort, siehe oben */
      } finally {
        setBusy(false);
      }
    },
    [loadHistory, emitSignals, withConversation, istNochAktuell],
  );

  return { messages, cards, steps, busy, loading, error, setError, send, runAction, approve, reject, loadHistory, reportContext };
}
